import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, TextField, InputAdornment,
  Dialog, DialogContent, DialogActions, Chip, Tooltip,
  CircularProgress, FormControlLabel, Switch,
  Grid2 as Grid, Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  servicesApi, tenantsApi,
  type ServiceDto, type AccountDto, type ServiceGroupDto, type UomDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";
import { AccountPicker } from "@/components/AccountPicker";
import { ServiceGroupPicker } from "@/components/ServiceGroupPicker";
import { UomPicker } from "@/components/UomPicker";
import { StatusPill } from "@/components/StatusPill";
import { useSearchParams } from "react-router-dom";
import { todayIso } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

export function ServicesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceDto | null>(null);
  const [ratesFor, setRatesFor] = useState<ServiceDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("sales.service.manage"));

  // Service Groups links here with ?groupId= to show one group's services.
  const [searchParams, setSearchParams] = useSearchParams();
  const groupId = searchParams.get("groupId") ?? undefined;

  const tenant = useQuery({
    queryKey: ["tenant", "current"],
    queryFn: tenantsApi.current,
    staleTime: 5 * 60_000,
  });
  const groupMapped = tenant.data?.serviceAccountMappingMode === "ServiceGroup";

  const query = useQuery({
    queryKey: ["services", search, groupId, pagination.page, pagination.pageSize],
    queryFn: () => servicesApi.list({
      search: search || undefined,
      serviceGroupId: groupId,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Service deleted" },
    mutationFn: (id: string) => servicesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["services"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (s: ServiceDto) => { setEditing(s); setFormOpen(true); };
  const handleDelete = (s: ServiceDto) => {
    if (confirm(`Delete service "${s.code} — ${s.name}"?`)) deleteMutation.mutate(s.id);
  };

  const columns: GridColDef<ServiceDto>[] = [
    { field: "code", headerName: "Code", width: 140 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
    {
      field: "serviceGroupName", headerName: "Group", width: 160,
      renderCell: (p) => p.row.serviceGroupName
        ?? <Box component="span" sx={{ color: "text.disabled" }}>Ungrouped</Box>,
    },
    { field: "sacCode", headerName: "SAC", width: 110 },
    {
      field: "currentRate", headerName: "Current rate", width: 130, align: "right", headerAlign: "right",
      valueFormatter: (v: number) => inr.format(v ?? 0),
    },
    {
      // Under group mapping the item's own head is not what posts, so show the resolved one and
      // flag the rows where nothing resolves at all.
      field: "glAccountCode", headerName: "Account head", width: 180,
      renderCell: (p) => {
        const usesGroupHead = groupMapped
          && !!p.row.effectiveGlAccountId
          && p.row.effectiveGlAccountId !== p.row.glAccountId;
        if (!p.row.effectiveGlAccountId) return <StatusPill label="Default 4300" tone="neutral" />;
        const label = usesGroupHead
          ? `${p.row.serviceGroupName ?? "Group"} head`
          : p.row.glAccountCode ? `${p.row.glAccountCode} — ${p.row.glAccountName ?? ""}` : "Mapped";
        return (
          <Tooltip title={usesGroupHead ? "Inherited from the service group" : ""}>
            <Box component="span">{label}</Box>
          </Tooltip>
        );
      },
    },
    {
      field: "gstRate", headerName: "Tax %", width: 90, align: "right", headerAlign: "right",
      valueFormatter: (v: number) => `${v ?? 0}%`,
    },
    {
      field: "isActive", headerName: "Status", width: 110,
      renderCell: (p) => (
        <Chip size="small" label={p.row.isActive ? "Active" : "Inactive"}
          color={p.row.isActive ? "success" : "default"} variant="outlined" />
      ),
    },
    {
      field: "actions", headerName: "", width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage, onClick: () => openEdit(p.row) },
          { label: "Rates & history", icon: <PriceChangeIcon fontSize="small" />, onClick: () => setRatesFor(p.row) },
          { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage, onClick: () => handleDelete(p.row) },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Catalog"]} title="Services"
        actions={<>{canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New service</Button>
        )}</>}
      />
      {groupId && (
        <Alert
          severity="info" sx={{ mb: 2 }}
          action={<Button size="small" onClick={() => setSearchParams({})}>Show all</Button>}
        >
          Showing services in one group only.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <ServiceDialog open={formOpen} onClose={() => setFormOpen(false)} service={editing} />
      <RatesDialog open={!!ratesFor} onClose={() => setRatesFor(null)} service={ratesFor} canManage={canManage} />
    </Box>
  );
}

type ServiceFormData = {
  code: string; name: string; sacCode: string;
  defaultRate: number; gstRate: number; glAccountId: string; isActive: boolean;
  serviceGroupId: string; unitOfMeasureId: string;
};
const emptyService: ServiceFormData = {
  code: "", name: "", sacCode: "", defaultRate: 0, gstRate: 18, glAccountId: "", isActive: true,
  serviceGroupId: "", unitOfMeasureId: "",
};

function ServiceDialog({ open, onClose, service }: { open: boolean; onClose: () => void; service: ServiceDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!service;
  const { taxLabel } = useTaxRegime();
  const [selectedAccount, setSelectedAccount] = useState<AccountDto | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<ServiceGroupDto | null>(null);
  const [selectedUom, setSelectedUom] = useState<UomDto | null>(null);
  const { control, handleSubmit, reset, formState: { errors } } =
    useForm<ServiceFormData>({ defaultValues: emptyService });

  const tenant = useQuery({
    queryKey: ["tenant", "current"],
    queryFn: tenantsApi.current,
    staleTime: 5 * 60_000,
  });
  const groupMapped = tenant.data?.serviceAccountMappingMode === "ServiceGroup";

  useEffect(() => {
    if (!open) return;
    // The pickers resolve themselves from initialId on the next render.
    setSelectedAccount(null);
    setSelectedGroup(null);
    setSelectedUom(null);
    reset(service ? {
      code: service.code, name: service.name, sacCode: service.sacCode ?? "",
      defaultRate: service.defaultRate, gstRate: service.gstRate,
      glAccountId: service.glAccountId ?? "", isActive: service.isActive,
      serviceGroupId: service.serviceGroupId ?? "",
      unitOfMeasureId: service.unitOfMeasureId ?? "",
    } : emptyService);
  }, [open, service, reset]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Service updated" : "Service created" },
    mutationFn: async (data: ServiceFormData) => {
      const payload = {
        code: data.code.trim(), name: data.name.trim(),
        sacCode: data.sacCode.trim() || undefined,
        defaultRate: data.defaultRate, gstRate: data.gstRate,
        glAccountId: data.glAccountId || null, isActive: data.isActive,
        serviceGroupId: data.serviceGroupId || null,
        unitOfMeasureId: data.unitOfMeasureId || null,
      };
      if (isEdit && service) { await servicesApi.update(service.id, payload); return service.id; }
      const r = await servicesApi.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["services"] });
      qc.invalidateQueries({ queryKey: ["service-groups"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
        <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit service" : "New service"}</ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="code" control={control} rules={{ required: "Required", maxLength: 50 }}
                render={({ field }) => (
                  <TextField {...field} label="Code *" fullWidth size="small" disabled={isEdit}
                    error={!!errors.code} helperText={errors.code?.message ?? (isEdit ? "Cannot change" : undefined)} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller name="name" control={control} rules={{ required: "Required", maxLength: 200 }}
                render={({ field }) => (
                  <TextField {...field} label="Name *" fullWidth size="small"
                    error={!!errors.name} helperText={errors.name?.message} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="serviceGroupId" control={control}
                render={({ field }) => (
                  <ServiceGroupPicker
                    value={selectedGroup}
                    initialId={field.value || undefined}
                    onChange={(v) => { setSelectedGroup(v); field.onChange(v?.id ?? ""); }}
                    helperText={groupMapped
                      ? "This tenant reads the account head from the group"
                      : "Optional — groups the service for rate cards and reporting"}
                  />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="unitOfMeasureId" control={control}
                render={({ field }) => (
                  <UomPicker
                    label="Charged per (unit)"
                    value={selectedUom}
                    initialId={field.value || undefined}
                    onChange={(v) => { setSelectedUom(v); field.onChange(v?.id ?? ""); }}
                    helperText="Default unit for new charge rows (visit, day, session…)"
                  />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="sacCode" control={control} rules={{ maxLength: 20 }}
                render={({ field }) => <TextField {...field} label="SAC code" fullWidth size="small" />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              {/* W9 — this number seeds the service's first charge-master row on create.
                  Billing reads the charge master, never this field, so changing it later
                  does not change what anyone is billed; use Rates & history for that. */}
              <Controller name="defaultRate" control={control}
                rules={{ min: { value: 0, message: ">= 0" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Starting rate (₹)" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                    error={!!errors.defaultRate}
                    helperText={errors.defaultRate?.message
                      ?? "Seeds the first rate. Later changes go through Rates & history."} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="gstRate" control={control}
                rules={{ min: { value: 0, message: "0-28" }, max: { value: 28, message: "0-28" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label={`${taxLabel} rate (%)`} fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                    error={!!errors.gstRate} helperText={errors.gstRate?.message} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller name="glAccountId" control={control}
                render={({ field }) => (
                  <AccountPicker
                    label="Revenue account head"
                    value={selectedAccount}
                    initialId={field.value || undefined}
                    onChange={(v) => { setSelectedAccount(v); field.onChange(v?.id ?? ""); }}
                    helperText={groupMapped
                      ? "Only used if the group above has no head of its own"
                      : "Service revenue posts here (when sales GL posting is enabled)"}
                  />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="isActive" control={control}
                render={({ field }) => (
                  <FormControlLabel label="Active"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />} />
                )} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Effective-dated rate history for a service — add a new rate from a date; older rates stay for back-dated bills. */
function RatesDialog({
  open, onClose, service, canManage,
}: { open: boolean; onClose: () => void; service: ServiceDto | null; canManage: boolean }) {
  const qc = useQueryClient();
  const [rate, setRate] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso);

  useEffect(() => {
    if (open) { setRate(""); setEffectiveFrom(todayIso()); }
  }, [open, service]);

  const ratesQuery = useQuery({
    queryKey: ["service-rates", service?.id],
    queryFn: () => servicesApi.rates(service!.id),
    enabled: open && !!service,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["service-rates", service?.id] });
    qc.invalidateQueries({ queryKey: ["services"] });
  };
  const addMutation = useMutation({
    meta: { successMessage: "Rate added" },
    mutationFn: () => servicesApi.addRate(service!.id, Number(rate), effectiveFrom),
    onSuccess: () => { setRate(""); invalidate(); },
  });
  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => servicesApi.setRateActive(id, isActive),
    onSuccess: invalidate,
  });

  const today = todayIso();
  const rows = ratesQuery.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ClosableDialogTitle onClose={onClose}>
        Rates — {service?.code} · {service?.name}
      </ClosableDialogTitle>
      <DialogContent dividers>
        {(addMutation.isError || toggleMutation.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(addMutation.error ?? toggleMutation.error)}</Alert>
        )}
        {canManage && (
          <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
            <TextField label="New rate (₹)" type="number" size="small" value={rate}
              onChange={(e) => setRate(e.target.value)} inputProps={{ min: 0, step: "0.01" }} sx={{ width: 160 }} />
            <TextField label="Effective from" type="date" size="small" value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" startIcon={<AddIcon />}
              disabled={rate === "" || Number(rate) < 0 || addMutation.isPending}
              onClick={() => addMutation.mutate()}>Add rate</Button>
          </Stack>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Effective from</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="center">Status</TableCell>
              {canManage && <TableCell align="right">Active</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => {
              const upcoming = r.effectiveFrom > today;
              return (
                <TableRow key={r.id} hover>
                  <TableCell>{r.effectiveFrom}{upcoming && <Chip size="small" label="Upcoming" sx={{ ml: 1 }} />}</TableCell>
                  <TableCell align="right">{inr.format(r.rate)}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" color={r.isActive ? "success" : "default"}
                      label={r.isActive ? "Active" : "Inactive"} variant="outlined" />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title={r.isActive ? "Deactivate" : "Activate"}>
                        <Switch size="small" checked={r.isActive}
                          onChange={(_, v) => toggleMutation.mutate({ id: r.id, isActive: v })} />
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {rows.length === 0 && !ratesQuery.isLoading && (
              <TableRow><TableCell colSpan={4}>
                <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>No rates recorded.</Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
